import { useId } from 'react';

import { FormField, FormSelect } from '@/components/form';

import { toolExecutionOverridesSchema } from '@repo/shared-types';

import type { ToolExecutionOverrides as Overrides } from '@repo/shared-types';

interface Props {
  tools: { name: string; label: string }[];
  value: unknown;
  defaultBehavior: unknown;
  asyncOnly: boolean;
  onChange: (value: Overrides | undefined) => void;
}

/** Configuration-scoped overrides, not mutations of shared custom tools. */
export const ToolExecutionOverrides = ({
  tools,
  value,
  defaultBehavior,
  asyncOnly,
  onChange,
}: Props) => {
  const id = useId();
  const parsed = toolExecutionOverridesSchema.safeParse(value ?? {});
  const overrides = parsed.success ? parsed.data : {};
  const options = new Map(tools.map((tool) => [tool.name, tool.label]));
  for (const name of Object.keys(overrides)) {
    if (!options.has(name)) options.set(name, `${name} (saved; not currently selected)`);
  }
  const inherited = asyncOnly || defaultBehavior !== 'blocking' ? 'non-blocking' : 'blocking';

  const update = (name: string, behavior: string) => {
    const next = { ...overrides };
    if (behavior === '') delete next[name];
    else if (behavior === 'blocking' || behavior === 'non_blocking') next[name] = behavior;
    onChange(Object.keys(next).length ? next : undefined);
  };

  return (
    <fieldset
      className="space-y-3 rounded-md border p-4 md:col-span-2"
      aria-describedby={`${id}-help`}
    >
      <legend className="px-1 text-sm font-medium">Per-tool execution overrides</legend>
      <p id={`${id}-help`} className="text-muted-foreground text-xs">
        {asyncOnly
          ? 'Extended Thinking runs every tool non-blocking. Saved blocking overrides are ignored on this model; you can clear them with Inherit.'
          : 'Inherit uses the configuration default. Non-blocking lets the assistant speak while the tool runs, but required customer confirmations and success checks still apply.'}
      </p>
      {options.size === 0 && (
        <p className="text-muted-foreground text-xs">
          Select tools or configure voice agents to add overrides.
        </p>
      )}
      <div className="grid gap-x-4 md:grid-cols-2">
        {[...options].map(([name, label], index) => (
          <FormField key={name} label={label} htmlFor={`${id}-${index}`}>
            <FormSelect
              id={`${id}-${index}`}
              aria-describedby={`${id}-help`}
              value={Object.prototype.hasOwnProperty.call(overrides, name) ? overrides[name] : ''}
              onChange={(event) => update(name, event.target.value)}
            >
              <option value="">Inherit configuration ({inherited})</option>
              <option value="non_blocking">Non-blocking — keep conversing</option>
              <option value="blocking" disabled={asyncOnly}>
                Blocking — wait for results{asyncOnly ? ' (unsupported)' : ''}
              </option>
            </FormSelect>
          </FormField>
        ))}
      </div>
    </fieldset>
  );
};
